import React, { useEffect, useRef, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Collapse,
	Divider,
	Fade,
	IconButton,
	Snackbar,
	TextField,
	Tooltip,
	TooltipProps,
	Typography,
	tooltipClasses,
} from '@mui/material';
import CustomDataGrid from '../CustomDataGrid/CustomDataGrid';
import SearchBar from '../SearchBar';
import CustomButton from '../CustomButton/CustomButton';
import { useAppSelector } from '@src/store/hooks';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import {
	ArrowBack,
	CrossIcon,
	DeleteIcon,
	EditIcon,
	InfoCircleIcon,
	LockIcon,
	TickIcon,
} from '../Icons';
import CustomModal from '../CustomModal';
import EmptyStateComponent from '../EmptyStateComponent/EmptyStateComponent';
import RemoveItems from '../RemoveItems/RemoveItems';
import SuppliersCatalog from '../SuppliersCatalog/SuppliersCatalog';
import PageHeader from '../PageHeader/PageHeader';

import { IsMobileWidth } from '../MediaQueryUtils';
import ProductDetails from '../ProductDetails/ProductDetails';
import { formatPriceValue, getUniqueObjectsByKey } from '@src/helpers/Helpers';
import { CustomAlertType, priceRegex } from '@src/pages/Catalogs';
import CustomDataGrid2 from '../CustomDataGrid/CustomDataGrid2';
import Lock from '@src/assets/icons/lock.svg';
import UnLock from '@src/assets/icons/unlock.svg';
import CustomAlert from '../CustomAlert/CustomAlert';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomPermission from '../Permissions/CustomPermission';
import Loading from '../Loading/Loading';
import { Category } from '@src/pages/MarketPlace';
import CustomTextButton from '../CustomButton/CustomTextButton';
import { SelectedItemsTextContainer } from '../CommonComponents/Common';
import CategoryCarousel from '../Carousal/CategoryCarousal/CategoryCarousal';
import PageHeaderMobile from '../PageHeader/PageHeaderMobile';
import DataNotFound from '../DataItemFound/DataNotFound';
import styled from 'styled-components';
import ProductCustomCard from '../CustomCard/ProductCustomCard';
import ProductCustomDetailView from '../CustomDetailView/ProductCustomDetailView';
import ProductCardSkeleton from '../CustomSkeleton/ProductCardSkeleton';
import CategorySliderSkeleton from '../CustomSkeleton/CategorySliderSkeleton';
import ProductCard from '../CustomCard/ProductCard';
import CustomFilter from '../CustomFilter/CustomFilter';
import { Filters, Product, TimeoutHandler } from '@src/interfaces/type';
import ProductList from '../ProductListing/ProductList';

const StyledContainer = styled('div')({
	width: '100%',
	'@media (max-width: 600px)': {
		// marginBottom: '68px',
	},
});

const StyledTextField = styled(TextField)`
	input {
		height: 47px;
		width: 151px;
		box-sizing: border-box;
		border: 1.5px solid #acacac;
		background: var(--chat-header-bg);
		text-align: center;
	}
`;

const StyledButtonWrapper = styled('div')({
	'@media (max-width: 478px)': {
		background: 'white',
		position: 'fixed',
		bottom: 0,
		zIndex: '100',
		width: '100%',
		padding: '16px',
		left: 0,
		borderRadius: '6px 6px 0px 0px',
		borderTop: '1.5px solid #F1F1F1',
	},
});

const SelectedItemsContainer = styled('div')({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '5px',
	fontSize: '16px',
	fontWeight: 750,
	lineHeight: '21.82px',
	transition: 'width 0.3s ease',
	padding: '16px',
});

const productDetailsModalStyles = {
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	maxWidth: '600px',
	maxHeight: '444px',
	'@media (max-width: 600px)': {
		width: '90%',
		maxWidth: '90%',
		maxHeight: '645px',
		height: '645px',
	},
};

const removeItemModalStyles = {
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	'@media (max-width: 600px)': {
		width: '306px',
	},
};

const StyledMobileContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	overflow: hidden;
	width: 100%;
	#filter-container {
		display: flex;
		gap: 10px;
		padding-left: 16px;
		padding-right: 16px;
		padding-bottom: 16px;
	}
`;

const ProductContainer = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	overflowY: 'auto',
	flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
});

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
`;

const longText = `
For some items like meats and fresh vegetables, the prices may change frequently. For such items, you can set a maximum price (you will accept) in the orderbook.
`;

const gridResponsiveStyles: any = {
	'@media (max-width: 478px)': {
		maxHeight: 'calc(100vh - 300px)',
		minHeight: 'calc(100vh - 300px)',
		paddingBottom: '25px',
	},
};

interface OrderBookProps {
	setOrderBook: any;
	waId: string;
	id: string;
}

function OrderBook({ setOrderBook, waId, id }: OrderBookProps) {
	const [isLoading, setIsLoading] = useState(true);
	const [btnLoading, setBtnLoading] = useState(false);
	const [disable, setDisable] = useState(false);
	const [searchInput, setSearchInput] = useState('');
	const [openModal, setOpenModal] = useState(false);
	const [productDetailsModal, setProductDetailsModal] = useState(false);
	const [supplierName, setSupplierName] = useState<string>('');
	const [productCatalog, showProductCatalog] = useState(false);
	const [orderbookProducts, setOrderbookProducts] = useState([]);
	const [selectedOrderBookProducts, setSelectedOrderBookProducts] = useState([]);
	const [catalogDetails, setCatalogDetails] = useState({ id: '', name: '' });
	const [draft, setDraft] = useState(false);
	const [productData, setProductData] = useState(null);
	const [isApiCall, setIsApiCall] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(1000);
	const [categories, setCategories] = useState<Category[]>([]);
	const [isErrorVisible, setErrorVisible] = useState(false);
	const [alert, setAlert] = useState<CustomAlertType>({ message: '', severity: 'success' });
	const [errorMessage, setErrorMessage] = useState('');
	const [collapse, setCollapse] = useState(false);
	const [productDetails, setProductDetails] = useState<Product | null>(null);
	const [detailsMiodal, setDetailsModal] = useState(false);
	const [filters, setFilters] = useState<Filters | null>(null);
	const [activeFilter, setActiveFilter] = useState('');
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [searching, setSearching] = useState<boolean>(false);
	const [loadingProducts, setLoadingProducts] = useState<boolean>(false);

	const timeoutHandler = useRef<TimeoutHandler>(null);

	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const chats = useAppSelector((state) => state.chats.value);
	const { apiService }: any = React.useContext(ApplicationContext);
	const { isDrawerOpen } = useAppSelector((state) => state.UI);
	const isMobileWidth = IsMobileWidth();

	//Permissions
	const checkPermission = usePermission();
	const canViewOrderBook = checkPermission(ALL_PERMISSION_MAP?.RESTAURANT?.ORDER_BOOK?.VIEW, waId);
	const canEditOrderBook = checkPermission(
		ALL_PERMISSION_MAP?.RESTAURANT?.ORDER_BOOK?.UPDATE,
		waId
	);
	const canDeleteOrderBook = checkPermission(
		ALL_PERMISSION_MAP?.RESTAURANT?.ORDER_BOOK?.DELETE,
		waId
	);

	const columns = [
		// { field: 'id', headerName: 'Product ID', width: 100 },
		{
			field: 'name',
			headerName: 'Part Name',
			width: 300,
			renderCell: (params: any) => {
				const name = params?.row?.name;
				return (
					<div
						style={{
							display: 'flex',
							gap: '8px',
							alignItems: 'center',
						}}
						title={name}
					>
						<img
							style={{ objectFit: 'contain' }}
							src={params?.row?.images?.[0]?.image || params?.row?.image || '/placeholderImage.svg'}
							alt="product-img"
							width="24px"
							height="24px"
						/>
						{params?.row?.name?.length > 33 ? (
							<CustomTooltip title={params?.row?.name} placement="top">
								<p
									style={{
										maxWidth: '260px',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{params?.row?.name}
								</p>
							</CustomTooltip>
						) : (
							<p
								style={{
									maxWidth: '260px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{params?.row?.name}
							</p>
						)}
					</div>
				);
			},
		},
		// { field: 'unit', headerName: 'Unit', width: 90 },
		{ field: 'groupCategory', headerName: 'Part Group Category', width: 180 },
		{
			field: 'category',
			headerName: 'Category',
			width: 100,
		},
		{
			field: 'subCategory',
			headerName: 'Sub Category',
			width: 100,
		},
		{ field: 'price', headerName: 'Price', width: 110 },
		{ field: 'customerPrice', headerName: 'Customer-Price', width: 130, align: 'center' },
		//new col
		{ field: 'brand', headerName: 'Brand', width: 150 },
		{ field: 'partNumber', headerName: 'Part Number', width: 150 },
		{ field: 'partDimension', headerName: 'Part Dimension ID', width: 180 },
		{ field: 'partDiameter', headerName: 'Part Diameter ID', width: 180 },
		{ field: 'partWeight', headerName: 'Part Weight', width: 150 },
		{ field: 'partQuality', headerName: 'Part Quality ID', width: 180 },
		{ field: 'partComposition', headerName: 'Part Composition ID', width: 180 },
		{ field: 'partDesign', headerName: 'Part Design ID', width: 180 },
		{ field: 'partPerformance', headerName: 'Part Performance ID', width: 180 },
		// { field: 'customerPrice', headerName: 'Customer-Price', width: 110 },
		// {
		// 	field: 'isContracted',
		// 	headerName: 'Contracted',
		// 	width: 100,
		// 	align: 'center',
		// 	renderCell: (params: any) =>
		// 		params?.row?.isContracted ? <img src={Lock} alt="contract-icon" /> : null,
		// },
	];

	const orderBookColumns = [
		...columns,
		{
			field: 'maxPrice',
			headerName: (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					Max Limit
					<CustomTooltip title={longText} placement="top">
						<IconButton>
							<InfoCircleIcon fill="#FFBE00" />
						</IconButton>
					</CustomTooltip>
				</div>
			),
			width: 160,
			renderCell: (params: any) =>
				params?.row?.edit ? (
					<StyledTextField
						id="editPrice"
						variant="standard"
						value={params?.row?.maxPrice}
						autoFocus={params?.row?.focus}
						onChange={(e) => {
							handleMaxPriceValueChange(e, params);
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleUpdate(params);
							}
						}}
						InputProps={{
							disableUnderline: true,
							classes: {
								root: 'MuiInputBase-root',
							},
						}}
					/>
				) : (
					params.row.maxPrice
				),
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'actions',
			headerName: 'Actions',
			width: 120,
			renderCell: (params: any) =>
				params?.row?.edit ? (
					<div
						id="updateBtns"
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '5px',
							width: '100%',
							height: '100%',
						}}
					>
						<IconButton onClick={() => handleUpdate(params)} disabled={disable}>
							<TickIcon id="updateProduct" />
						</IconButton>
						<IconButton onClick={() => handleEditClose(params)}>
							<CrossIcon id="cancelProduct" />
						</IconButton>
					</div>
				) : (
					<div
						id="editBtns"
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%',
							height: '100%',
						}}
					>
						{canEditOrderBook ? (
							<IconButton
								disabled={params?.row?.editBtnDisable}
								onClick={() => handleEditClick(params)}
							>
								<EditIcon id="editProduct" />
							</IconButton>
						) : (
							<CustomTooltip
								title="You do not have permission to edit orderbook. Contact 
					an administrator to get permission."
								placement="bottom"
							>
								<span>
									<IconButton
										disabled={params?.row?.editBtnDisable || !canDeleteOrderBook}
										onClick={() => {}}
									>
										<EditIcon id="editProduct" />
									</IconButton>
								</span>
							</CustomTooltip>
						)}
					</div>
				),
			align: 'center',
			headerAlign: 'center',
		},
	];

	const displayCustomError = (msg: string) => {
		setErrorMessage(msg);
		setErrorVisible(true);
	};

	const handleErrorClose = () => {
		setErrorMessage('');
		setErrorVisible(false);
	};

	const handleEditClick = (params: any) => {
		const updatedProducts: any = orderbookProducts?.map((product: any) => {
			if (params?.id === product.id) {
				return { ...product, edit: true, focus: true, tempMaxLimit: product?.maxPrice };
			}
			return product;
		});
		setOrderbookProducts(updatedProducts);
	};

	const handleEditClose = (params: any) => {
		const updatedProducts: any = orderbookProducts?.map((product: any) => {
			if (params?.id === product.id) {
				return { ...product, edit: false, maxPrice: product?.tempMaxLimit };
			}
			return product;
		});
		setOrderbookProducts(updatedProducts);
	};

	const handleMaxPriceValueChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		params: any
	) => {
		const inputValue = e.target.value;

		if (priceRegex.test(inputValue)) {
			const updatedProducts: any = orderbookProducts?.map((product: any) => {
				if (params.row.id === product.id) {
					return { ...product, maxPrice: inputValue };
				}
				return product;
			});

			setOrderbookProducts(updatedProducts);
		}
	};
	const handleUpdateMaxLimit = async (item: any, value: any) => {
		setDisable(true);
		let data = {};
		const updatedProducts: any = orderbookProducts?.map((product: any) => {
			if (item?.id === product.id) {
				const maxPrice = value ? formatPriceValue(value) : 0;
				data = {
					productId: product.id,
					maxPrice: maxPrice,
				};
				return {
					...product,
					edit: false,
					focus: false,
					maxPrice: maxPrice,
				};
			}
			return product;
		});

		try {
			await apiService.updateProductMaxLimit(
				data,
				(response: AxiosResponse) => {
					setOrderbookProducts(updatedProducts);
					setAlert({
						message: 'Item updated successfully',
						severity: 'success',
					});
					setDisable(false);
				},
				(error: AxiosError) => {
					console.log(error);
					displayCustomError('Something went wrong!');
					setDisable(false);
				},
				waId,
				id
			);
			return true;
		} catch (error) {
			console.log(error);
			displayCustomError('Something went wrong!');
			setDisable(false);
			return false;
		}
	};

	const handleUpdate = (params: any) => {
		setDisable(true);
		let data = {};
		const updatedProducts: any = orderbookProducts?.map((product: any) => {
			if (params.row.id === product.id) {
				const maxPrice = product?.maxPrice ? formatPriceValue(product?.maxPrice) : 0;
				data = {
					productId: product.id,
					maxPrice: maxPrice,
				};
				return {
					...product,
					edit: false,
					focus: false,
					maxPrice: maxPrice,
				};
			}
			return product;
		});

		apiService.updateProductMaxLimit(
			data,
			(response: AxiosResponse) => {
				setOrderbookProducts(updatedProducts);
				setAlert({
					message: 'Item updated successfully',
					severity: 'success',
				});
				setDisable(false);
			},
			(error: AxiosError) => {
				console.log(error);
				displayCustomError('Something went wrong!');
				setDisable(false);
			},
			waId,
			id
		);
	};
	const pageContainerStyles: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		width:
			isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 240px)'
				: !isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 64px)'
				: '100%',
	};

	const handleArrowBack = () => {
		if (productCatalog) {
			showProductCatalog(false);
		} else {
			setOrderBook(false);
		}
	};

	// useEffect(() => {
	// 	window.addEventListener('popstate', () => {
	// 		handleArrowBack();
	// 	});
	// 	return () => {
	// 		window.removeEventListener('popstate', () => {
	// 			return;
	// 		});
	// 	};
	// }, [productCatalog]);

	const getOrderbook = (supplierWaId: any, id: string, pageNumber?: number) => {
		const limit = 25;
		apiService.getOrderBook(
			(response: AxiosResponse) => {
				const data = response?.data?.data;
				const modifiedRowsData = data?.items?.map((item: any) => ({
					...item,
					name: item?.productName,
					categoryId: item?.category?.id,
					category: item?.category?.name,
					categoryObject: { id: item?.category?.id, value: item?.category?.name },
					id: item?.productId?.toString(),
					customerPrice: item?.customerPrice,
					price: item?.price,
					description: item?.description,
					isContracted: item?.isContracted,
					maxPrice: item?.maxPrice || null,
					subCategory: item?.subCategory?.name,
					subCategoryId: item?.subCategory?.id,
					machineBrand: item?.machineBrand?.name,
					machineBrandId: item?.machineBrand?.id,
					machineModel: item?.machineModel?.name,
					machineModelId: item?.machineModel?.id,
					groupCategory: item?.groupCategory?.name,
					groupCategoryId: item?.groupCategory?.id,
					brand: item?.brand?.name,
					brandId: item?.brand?.id,
					partComposition: item?.partComposition?.name,
					partCompositionId: item?.partComposition?.id,
					partDesign: item?.partDesign?.name,
					partDesignId: item?.partDesign?.id,
					partDiameter: item?.partDiameter?.name,
					partDiameterId: item?.partDiameter?.id,
					partDimension: item?.partDimension?.name,
					partDimensionId: item?.partDimension?.id,
					partOwnership: item?.partOwnership?.name,
					partOwnershipId: item?.partOwnership?.id,
					partQuality: item?.partQuality?.name,
					partQualityId: item?.partQuality?.id,
					partWeight: item?.partWeight?.name,
					partWeightId: item?.partWeight?.id,
					partPerformance: item?.partPerformance?.name,
					partPerformanceId: item?.partPerformance?.id,
				}));

				const totalCount: number = data?.totalCount;
				const totalPages: number = Math.max(Math.ceil(totalCount / limit), 1);

				setTotalPages(totalPages);
				setTotalCount(totalCount);

				if (pageNumber) {
					setPage(1);
				}

				if (page === 1) {
					setOrderbookProducts(modifiedRowsData);
				} else {
					//@ts-ignore
					setOrderbookProducts((prevState: any) => [...prevState, ...modifiedRowsData]);
				}

				setCatalogDetails({
					name: response?.data?.data?.name,
					id: response?.data?.data?.catalogId,
				});

				setIsLoading(false);
				setLoadingProducts(false);
				setSearching(false);
			},
			(error: AxiosError) => {
				setIsLoading(false);
				setLoadingProducts(false);
				setSearching(false);
				displayCustomError('Something went wrong!');
			},
			supplierWaId,
			id,
			{
				page: page,
				limit: limit,
				category: Number(selectedIndex) === 1000 ? '' : selectedIndex,
				search: searchInput,
				machineModel: filters?.model?.id,
				subCategory: filters?.subCategory?.id,
				machineBrand: filters?.machineBrand?.id,
				dimension: filters?.dimension?.id,
				design: filters?.design?.id,
				weight: filters?.weight?.id,
				composition: filters?.composition?.id,
				brand: filters?.brand?.id,
			}
		);
	};

	const onRowsSelectionHandler = (ids: String[]) => {
		const data: any[] = ids.map((id: any) => orderbookProducts.find((row: any) => row.id === id));
		//@ts-ignore
		setSelectedOrderBookProducts(data);
	};

	const handleRowClick = (params: any, event: any) => {
		const id = event?.target?.id;
		if (
			![
				'editProduct',
				'cancelProduct',
				'updateProduct',
				'editPrice',
				'editBtns',
				'updateBtns',
				'editProduct1',
			]?.includes(id) &&
			!params?.row?.edit
		) {
			const details: any = [
				{
					name: params?.row?.name,
					image: params?.row?.image || '/product.png',
					images: params?.row?.images?.map((image: any) => {
						return { src: image?.image, alt: 'product-img' };
					}),
					description: params?.row?.description,
					details: [
						`Part Category: ${params?.row?.category}`,
						`Unit: ${params?.row?.unit}`,
						`Price: ${params?.row?.price}`,
					],
				},
			];
			setProductData(details);
			setProductDetailsModal(true);
			// 	}
		}
	};

	const removeFromOrderBook = () => {
		setBtnLoading(true);

		const data = selectedOrderBookProducts?.map((ele: any) => Number(ele?.id || ele));

		apiService.removeItemsFromOrderBook(
			data,
			(response: AxiosResponse) => {
				setSelectedOrderBookProducts([]);
				setOpenModal(false);
				setBtnLoading(false);
				setAlert({
					message: 'Item removed from orderbook',
					severity: 'success',
				});

				//next call
				getOrderbook(waId, id, 1);
			},
			(error: AxiosError) => {
				setOpenModal(false);
				setBtnLoading(false);
				displayCustomError('Something went wrong!');
			},
			waId,
			id
		);
	};
	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchInput(value);
		setPage(1);
	};

	const showDetails = (product: any) => {
		setProductDetails(product);
		setDetailsModal(true);
	};

	const handleCategoryChange = (category: Category) => {
		if (selectedIndex !== category?.category_id) {
			setSelectedIndex(category?.category_id as number);

			if (category?.category_id !== 1000) {
				const products: any = orderbookProducts?.filter(
					(item: any) => item?.categoryId === category?.category_id
				);
				setOrderbookProducts(products);
			} else {
				setOrderbookProducts(orderbookProducts);
			}
		}
	};

	const loadMore = () => {
		setLoadingProducts(true);
		//@ts-ignore
		setPage((prevState: number) => prevState + 1);
	};

	const viewDetails = (product: Product, e?: any) => {
		if (e?.target?.tagName === 'INPUT' || ['editIcon']?.includes(e?.target?.id)) {
			return;
		}
		setProductDetailsModal(true);
		setProductDetails(product);
	};

	const filterCheck = () => {
		return (
			filters?.model?.id ||
			filters?.subCategory?.id ||
			filters?.machineBrand?.id ||
			filters?.dimension?.id ||
			filters?.design?.id ||
			filters?.weight?.id ||
			filters?.composition?.id ||
			filters?.brand?.id
		);
	};

	const btnView = () => {
		if (selectedOrderBookProducts?.length) {
			return (
				<CustomButton
					title="Delete Item(s)"
					handleClick={() => setOpenModal(true)}
					styles={{
						width: '320px',
						height: '40px',
						background: '#F26184 !important',
						color: '#FFFFFF',
						transition: 'background 0.3s ease, color 0.3s ease',
					}}
				/>
			);
		}
		return (
			<CustomButton
				title="Add New Product"
				handleClick={() => {
					showProductCatalog(true);
				}}
				styles={{
					width: '320px',
					height: '40px',
					transition: 'background 0.3s ease, color 0.3s ease',
				}}
			/>
		);
	};
	const handleSpecificProductSelect = (id: any) => {
		const ids: string[] = [];

		setOrderbookProducts((prevState: any) => {
			return prevState?.map((item: any) => {
				if ((item?.id !== id && item?.isSelection) || (item?.id === id && !item?.isSelection)) {
					ids.push(item?.id);
				}
				if (item?.id === id) {
					return {
						...item,
						isSelection: !item?.isSelection,
					};
				}
				return item;
			});
		});
		//@ts-ignore
		setSelectedOrderBookProducts(ids);
	};

	const handleSelectAll = (products: any) => {
		const ids: string[] =
			selectedOrderBookProducts?.length === orderbookProducts?.length
				? []
				: orderbookProducts?.map((item: any) => item?.id);
		setOrderbookProducts((prevState: any) => {
			return prevState?.map((item: any) => {
				return {
					...item,
					isSelection: !(selectedOrderBookProducts?.length === orderbookProducts?.length),
				};
			});
		});

		//@ts-ignore
		setSelectedOrderBookProducts(ids);
	};

	const setCategoriesforSlider = (supplierProductsResponse: any) => {
		apiService.getProductCategories(
			(res: AxiosResponse) => {
				const data = [{ category_id: 1000, name: 'All', image: '/Icons/All.png' }];

				setCategories([...data, ...res?.data?.data?.categories]);
			},
			(error: AxiosError) => {
				displayCustomError('Something went wrong');
			}
		);
	};

	useEffect(() => {
		if (canViewOrderBook) {
			getOrderbook(waId, id);
			for (const chatId in chats) {
				if (chats.hasOwnProperty(chatId) && chats[chatId].waId === waId) {
					//@ts-ignore
					setSupplierName(chats[chatId]?.name);
					break;
				}
			}
		}
	}, [page, filters]);

	useEffect(() => {
		//debounce
		if (timeoutHandler.current) {
			clearTimeout(timeoutHandler.current);
		}
		timeoutHandler.current = setTimeout(() => {
			try {
				getOrderbook(waId, id);
			} catch (err) {}
		}, 500);
	}, [searchInput]);

	if (isLoading) {
		// if (isMobileWidth) {
		// 	return (
		// 		<StyledMobileContainer>
		// 			<PageHeaderMobile
		// 				title="My Order Book"
		// 				setCollapse={setCollapse}
		// 				collapse={collapse}
		// 				onClick={() => {
		// 					showProductCatalog(false);
		// 				}}
		// 			/>
		// 			<CategorySliderSkeleton />
		// 			<ProductContainer>
		// 				{[1, 2, 3]?.map((item) => {
		// 					return <ProductCardSkeleton key={item} />;
		// 				})}
		// 			</ProductContainer>
		// 		</StyledMobileContainer>
		// 	);
		// }
		return (
			<StyledContainer>
				<Loading />
			</StyledContainer>
		);
	}

	const snackbarMsgs = () => {
		return (
			<>
				{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
				<Snackbar
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					open={isErrorVisible}
					autoHideDuration={6000}
					onClose={handleErrorClose}
				>
					<Alert onClose={handleErrorClose} severity="error" elevation={4}>
						{errorMessage}
					</Alert>
				</Snackbar>
			</>
		);
	};

	const modals = () => {
		if (detailsMiodal && productDetails) {
			return (
				<CustomModal
					open={detailsMiodal}
					setOpen={setDetailsModal}
					header={false}
					style={productDetailsModalStyles}
				>
					{/* @ts-ignore */}
					<ProductDetails data={productDetails} setOpenModal={setDetailsModal} />
				</CustomModal>
			);
		}
	};

	return productCatalog ? (
		<>
			<SuppliersCatalog
				setOrderBook={setOrderBook}
				productCatalog={productCatalog}
				showProductCatalog={showProductCatalog}
				waId={waId}
				columns={columns}
				catalogDetails={catalogDetails}
				supplierName={supplierName}
				draft={draft}
				pageContainerStyles={pageContainerStyles}
				categories={categories}
				setCategories={setCategories}
				selectedIndex={selectedIndex}
				setSelectedIndex={setSelectedIndex}
				displayCustomError={displayCustomError}
				setAlert={setAlert}
				getOrderbook={getOrderbook}
				showDetails={showDetails}
			/>
			{snackbarMsgs()}
			{modals()}
		</>
	) : canViewOrderBook ? (
		<StyledContainer style={pageContainerStyles}>
			<PageHeader activeIcon={true} title="My Order Book" handleArrowBack={handleArrowBack} />
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				sx={{
					m: 2,
					textTransform: 'capitalize',
					'@media (max-width: 768px)': {
						mb: '0px',
						alignItems: 'flex-start',
					},
				}}
			>
				<Box
					sx={{
						width: '315px',
						'@media (max-width: 550px)': {
							width: '100%',
						},
					}}
				>
					<SearchBar isLoading={false} value={searchInput} onChange={(val) => handleSearch(val)} />
				</Box>
				<StyledButtonWrapper>
					{canEditOrderBook ? (
						orderbookProducts?.length === 0 ? null : (
							<CustomButton
								title="Add New Product"
								heightVariant="medium"
								color="primary"
								handleClick={() => {
									showProductCatalog(true);
								}}
								styles={{
									marginRight: '15px',
									width: '162px',
									'@media (max-width: 478px)': {
										width: '100%',
									},
								}}
							/>
						)
					) : (
						<CustomTooltip
							title="You do not have permission to edit orderbook. Contact 
						an administrator to get permission."
							placement="bottom"
						>
							<span>
								<CustomButton
									title="Add New Product"
									heightVariant="medium"
									color="primary"
									handleClick={() => {}}
									styles={{
										marginRight: '15px',
										width: '162px',
										'@media (max-width: 478px)': {
											width: '100%',
										},
									}}
									disabled
								/>
							</span>
						</CustomTooltip>
					)}
				</StyledButtonWrapper>
			</Box>
			<Box
				display="flex"
				justifyContent="flex-end"
				alignItems="center"
				sx={{
					px: 2,
					'@media (max-width: 550px)': {
						flexDirection: 'column',
						alignItems: 'flex-end',
						minHeight: isMobileWidth
							? selectedOrderBookProducts?.length
								? 'unset'
								: '0px'
							: '86px',
					},
				}}
			>
				{selectedOrderBookProducts?.length >= 1 &&
				!productCatalog &&
				orderbookProducts?.length !== 0 ? (
					<Box>
						{selectedOrderBookProducts?.length} items selected
						<IconButton
							onClick={() => setOpenModal(true)}
							sx={{
								cursor: 'poinnter',
								py: 0,
								pl: 2,
								':hover': { backgroundColor: 'transparent' },
							}}
						>
							<DeleteIcon />
						</IconButton>
					</Box>
				) : null}
			</Box>

			<Box
				sx={
					isMobileWidth
						? {
								height: '72vh',
								// paddingBottom: '200px',
								overflow: 'auto',
						  }
						: {
								overflow: 'auto',
						  }
				}
			>
				<CustomFilter
					filters={filters}
					setFilters={setFilters}
					activeFilter={activeFilter}
					setActiveFilter={setActiveFilter}
					products={orderbookProducts}
					setFilteredProducts={setOrderbookProducts}
					setLoading={setSearching}
					filterType="server"
					setPage={setPage}
				/>
				<Divider />
				{isLoading ? (
					<Box
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<div className="chat__body__loadingMore__wrapper">
							<CircularProgress size={28} />
						</div>
					</Box>
				) : orderbookProducts?.length === 0 && !(searchInput || filterCheck()) ? (
					//@ts-ignore
					<EmptyStateComponent showProductCatalog={showProductCatalog} />
				) : orderbookProducts?.length ? (
					//@ts-ignore
					<ProductList
						//@ts-ignore
						products={orderbookProducts}
						searching={searching}
						loadingProducts={loadingProducts}
						loadMore={loadMore}
						handleSpecificProductSelect={handleSpecificProductSelect}
						showDetails={showDetails}
						style={{
							// overflow: 'auto',
							height: 'calc(100vh - 280px)',
							maxHeight: 'calc(100vh - 280px)',
						}}
						totalCount={totalCount}
					/>
				) : (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: 'calc(100vh - 340px)',
						}}
					>
						No Products Found
					</div>
				)}
			</Box>
			{orderbookProducts?.length !== 0 ? (
				<SelectedItemsContainer style={{ justifyContent: 'flex-end', marginRight: '16px' }}>
					<Typography>Total Items: {totalCount}</Typography>
				</SelectedItemsContainer>
			) : null}

			<CustomModal
				open={openModal}
				setOpen={setOpenModal}
				title="Remove Items"
				style={removeItemModalStyles}
			>
				<RemoveItems
					setOpenModal={setOpenModal}
					handleConfirmButton={removeFromOrderBook}
					supplierName={supplierName}
					btnLoading={btnLoading}
					totalItems={selectedOrderBookProducts?.length}
				/>
			</CustomModal>

			{snackbarMsgs()}
			{modals()}
		</StyledContainer>
	) : (
		<StyledContainer style={pageContainerStyles}>
			<CustomPermission
				icon={<LockIcon />}
				title="We are sorry..."
				paragraph="You do not have permission to view orderbook. Contact 
					an administrator to get permission."
			/>
			{snackbarMsgs()}
		</StyledContainer>
	);
}

export default OrderBook;
